import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { categoryMap } from "@/mocks/categories";
import { formatDate } from "@/lib/content";

interface HeroCarouselProps {
  slides: Article[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = useCallback(() => {
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const timer = window.setInterval(next, 6000);
    return () => window.clearInterval(timer);
  }, [next, paused, slides.length]);

  if (slides.length === 0) return null;

  const current = slides[index];

  return (
    <section
      aria-label="焦点内容"
      className="group relative h-[440px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[560px] lg:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, slideIndex) => (
        <img
          key={slide.id}
          src={slide.cover}
          alt={`${slide.title} - 焦点推荐`}
          title={`${slide.title} - 抖阳`}
          className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/55 to-foreground-950/20" />

      <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-4 md:px-7 md:pt-6">
        <span className="flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 font-label text-[11px] uppercase tracking-[0.2em] text-background-50 backdrop-blur-sm">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-400" />
          Focus / 焦点
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={prev}
            aria-label="上一张"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50 hover:text-foreground-900"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="下一张"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50 hover:text-foreground-900"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div key={current.id} className="absolute inset-x-0 bottom-0 animate-fade-up px-4 pb-6 md:px-7 md:pb-9">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="rounded bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
            {categoryMap[current.category]?.name}
          </span>
          {current.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded border border-background-50/40 px-2 py-1 text-[11px] text-background-100"
            >
              {tag}
            </span>
          ))}
        </div>
        <h2 className="max-w-3xl font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
          <Link to={`/detail/article/${current.id}`} className="transition-colors hover:text-primary-200">
            {current.title}
          </Link>
        </h2>
        <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block md:text-base">
          {current.subtitle}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-300">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" />
            {current.author.name}
          </span>
          <span>{formatDate(current.date)}</span>
          <span>{current.readMinutes} 分钟阅读</span>
          <Link
            to={`/detail/article/${current.id}`}
            className="ml-auto hidden cursor-pointer items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-xs font-medium text-foreground-950 transition-colors hover:bg-primary-100 md:flex"
          >
            阅读全文
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="mt-5 flex items-center gap-2">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setIndex(slideIndex)}
              aria-label={`切换到第 ${slideIndex + 1} 张`}
              className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                slideIndex === index ? "w-8 bg-primary-400" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}